// Central static content for Akyvia Sciences and Technologies

import {
  FlaskConical,
  Cpu,
  BrainCircuit,
  Code2,
  Bot,
  Factory,
  Plane,
  Rocket,
  Telescope,
  SunMedium,
  Zap,
  BookOpen,
  Cog,
} from "lucide-react";

export const COMPANY = {
  name: "Akyvia Sciences and Technologies Private Limited",
  short: "Akyvia",
  tagline: "Innovating Science. Engineering Tomorrow.",
  email: "akyvia.st@gmail.com",
  phone: "+91 99883 30113",
  phoneRaw: "+919988330113",
  address:
    "SCO 113, Gilco Valley Shopping Complex, Gilco Valley, Sector-115, Mohali-140301, Punjab, India",
  addressShort: "Sector-115, Mohali, Punjab, India",
  mapEmbed:
    "https://www.google.com/maps?q=Sector%20115%20Mohali%20Punjab&output=embed",
  social: {
    linkedin: "https://linkedin.com/",
    twitter: "https://twitter.com/",
    youtube: "https://youtube.com/",
    github: "https://github.com/",
  },
};

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Research", to: "/research" },
  { label: "Technologies", to: "/technologies" },
  { label: "Products", to: "/products" },
  { label: "Manufacturing", to: "/manufacturing" },
  { label: "Training", to: "/training" },
  { label: "Projects", to: "/projects" },
  { label: "Careers", to: "/careers" },
  { label: "News", to: "/news" },
  { label: "Contact", to: "/contact" },
];

export const BUSINESS_AREAS = [
  {
    icon: FlaskConical,
    title: "Research & Development",
    desc: "Applied scientific research turning experimental ideas into engineered products.",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    icon: Cog,
    title: "Special Electric Motors",
    desc: "Custom BLDC, PMSM and precision motors for industrial, aerospace and defense.",
  },
  {
    icon: BrainCircuit,
    title: "AI Solutions",
    desc: "Vision, predictive analytics and autonomous decision systems for industry.",
  },
  {
    icon: Code2,
    title: "System Software",
    desc: "Real-time firmware, control stacks and mission-critical software.",
  },
  {
    icon: Cpu,
    title: "Embedded Systems",
    desc: "Board bring-up, RTOS, sensor fusion and edge compute platforms.",
  },
  {
    icon: Bot,
    title: "Robotics",
    desc: "Mobile, articulated and collaborative robotics for research & industry.",
    span: "lg:col-span-2",
  },
  {
    icon: Factory,
    title: "Industrial Automation",
    desc: "PLC, SCADA and IIoT solutions for smart, connected factories.",
  },
  {
    icon: Plane,
    title: "Aviation Technologies",
    desc: "UAVs, autopilots, flight controllers and navigation stacks.",
  },
  {
    icon: Rocket,
    title: "Space Sciences",
    desc: "Instrumentation, satellite subsystems and experimental platforms.",
  },
  {
    icon: Telescope,
    title: "Astronomy",
    desc: "Telescope engineering, astrophotography and scientific outreach.",
  },
  {
    icon: SunMedium,
    title: "Renewable Energy",
    desc: "Solar, storage and clean-energy motor drive systems.",
  },
  {
    icon: Zap,
    title: "Power Systems",
    desc: "Power electronics, smart grid and industrial drive technologies.",
  },
  {
    icon: BookOpen,
    title: "Engineering Consultancy",
    desc: "Design, prototyping and technology commercialization services.",
    span: "lg:col-span-2",
  },
];

export const PRODUCTS = [
  {
    slug: "bldc-precision-motor-ak-1200",
    name: "AK-1200 BLDC Precision Motor",
    category: "Special Electric Motors",
    image:
      "https://images.unsplash.com/photo-1581092334651-ddf26d9a09d0?w=1200&q=80&auto=format&fit=crop",
    description:
      "High-torque BLDC motor engineered for precision industrial and aerospace actuation.",
    applications: ["Robotics", "UAV actuation", "Precision CNC", "Servo systems"],
    specs: {
      "Rated Power": "1.2 kW",
      "Rated Speed": "6,000 RPM",
      "Peak Torque": "4.8 Nm",
      "Voltage": "48 V DC",
      "Efficiency": "> 92%",
    },
  },
  {
    slug: "motion-controller-mx-9",
    name: "MX-9 Intelligent Motion Controller",
    category: "Controllers",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&q=80&auto=format&fit=crop",
    description:
      "9-axis synchronized motion controller with field-oriented control and CAN/EtherCAT.",
    applications: ["Robotics", "Automation", "Motion research", "AGVs"],
    specs: {
      "Axes": "9",
      "Loop Rate": "16 kHz",
      "Interfaces": "CAN, EtherCAT, USB-C",
      "SDK": "C++, Python",
    },
  },
  {
    slug: "edge-ai-node-nx-1",
    name: "NX-1 Edge AI Compute Node",
    category: "AI Systems",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&q=80&auto=format&fit=crop",
    description:
      "Ruggedized edge compute node for machine vision and predictive maintenance.",
    applications: ["Machine vision", "Predictive maintenance", "Robotics"],
    specs: {
      "Compute": "40 TOPS",
      "Memory": "16 GB LPDDR5",
      "Storage": "512 GB NVMe",
      "Power": "15W typical",
    },
  },
  {
    slug: "embedded-controller-ec-32",
    name: "EC-32 Embedded Controller",
    category: "Embedded Devices",
    image:
      "https://images.unsplash.com/photo-1555617981-dac3880eac6e?w=1200&q=80&auto=format&fit=crop",
    description:
      "ARM Cortex-M based embedded controller for industrial IoT and sensor fusion.",
    applications: ["IIoT", "Sensor fusion", "Robotics", "Data acquisition"],
    specs: {
      "MCU": "ARM Cortex-M7 @ 480 MHz",
      "RAM": "1 MB",
      "Radios": "Wi-Fi 6, BLE 5.3, LoRa",
      "IO": "48 GPIO, 8 ADC, 4 CAN",
    },
  },
  {
    slug: "industrial-vfd-iv-22",
    name: "IV-22 Industrial VFD Drive",
    category: "Industrial Electronics",
    image:
      "https://images.unsplash.com/photo-1717386255773-1e3037c81788?w=1200&q=80&auto=format&fit=crop",
    description:
      "22 kW variable frequency drive with sensorless vector control and IIoT telemetry.",
    applications: ["Pumps", "Conveyors", "CNC spindles", "Renewable inverters"],
    specs: {
      "Power": "22 kW",
      "Input": "3-Phase 380-480 V",
      "Overload": "150% / 60 s",
      "Comms": "Modbus, EtherNet/IP",
    },
  },
  {
    slug: "smart-plc-sp-io",
    name: "SP-IO Smart PLC Gateway",
    category: "Smart Automation Systems",
    image:
      "https://images.unsplash.com/photo-1581091012184-7c1b17c86e64?w=1200&q=80&auto=format&fit=crop",
    description:
      "Compact edge-PLC with cloud gateway for factory automation and remote monitoring.",
    applications: ["Smart factory", "Remote monitoring", "Energy telemetry"],
    specs: {
      "I/O": "64 DI / 32 DO / 8 AI / 4 AO",
      "CPU": "Dual-Core ARM",
      "Cloud": "MQTT, HTTPS, OPC-UA",
    },
  },
  {
    slug: "uav-flight-controller-fx-1",
    name: "FX-1 UAV Flight Controller",
    category: "Aviation Equipment",
    image:
      "https://images.unsplash.com/photo-1508614589041-895b88991e3e?w=1200&q=80&auto=format&fit=crop",
    description:
      "Redundant, GPS-INS fused flight controller for research and industrial UAVs.",
    applications: ["Research UAVs", "Surveying", "Agricultural drones"],
    specs: {
      "IMU": "Triple redundant",
      "GNSS": "L1/L2 RTK",
      "Interfaces": "CAN, UART, PWM x16",
      "Firmware": "Open architecture",
    },
  },
  {
    slug: "spectroscope-sx-4",
    name: "SX-4 Compact Spectroscope",
    category: "Scientific Instruments",
    image:
      "https://images.unsplash.com/photo-1727091506059-69012c069173?w=1200&q=80&auto=format&fit=crop",
    description:
      "Compact optical spectroscope for academic laboratories and field science.",
    applications: ["Education", "Field research", "Astronomy outreach"],
    specs: {
      "Range": "350–900 nm",
      "Resolution": "1.2 nm",
      "Interface": "USB-C",
    },
  },
];

export const TRAINING_PROGRAMS = [
  {
    name: "Artificial Intelligence",
    duration: "12 weeks",
    level: "Beginner → Advanced",
    tag: "AI/ML",
  },
  { name: "Machine Learning", duration: "10 weeks", level: "Intermediate", tag: "AI/ML" },
  { name: "Embedded Systems", duration: "8 weeks", level: "Beginner", tag: "Systems" },
  { name: "Robotics", duration: "12 weeks", level: "Intermediate", tag: "Robotics" },
  { name: "Electric Motors", duration: "6 weeks", level: "All Levels", tag: "Power" },
  { name: "Power Electronics", duration: "8 weeks", level: "Intermediate", tag: "Power" },
  { name: "Industrial Automation", duration: "10 weeks", level: "All Levels", tag: "Automation" },
  { name: "Drone Technology", duration: "6 weeks", level: "Beginner", tag: "Aviation" },
  { name: "Aviation Engineering", duration: "12 weeks", level: "Advanced", tag: "Aviation" },
  { name: "Space Sciences", duration: "10 weeks", level: "Intermediate", tag: "Space" },
  { name: "Astronomy", duration: "6 weeks", level: "Beginner", tag: "Space" },
  { name: "Research Methodology", duration: "4 weeks", level: "All Levels", tag: "Research" },
  { name: "Engineering Design", duration: "8 weeks", level: "Intermediate", tag: "Design" },
  { name: "Software Development", duration: "10 weeks", level: "Beginner → Advanced", tag: "Software" },
];

export const PROJECTS = [
  {
    title: "Autonomous Warehouse Robot",
    status: "Completed",
    year: "2024",
    domain: "Robotics",
    image:
      "https://images.unsplash.com/photo-1716191299980-a6e8827ba10b?w=1200&q=80&auto=format&fit=crop",
    desc: "AMR platform with LiDAR SLAM, vision-based bin picking and fleet orchestration.",
  },
  {
    title: "UAV Autopilot for Agriculture",
    status: "Completed",
    year: "2024",
    domain: "Aviation",
    image:
      "https://images.unsplash.com/photo-1508614589041-895b88991e3e?w=1200&q=80&auto=format&fit=crop",
    desc: "Precision spraying UAV with variable-rate control and RTK-grade navigation.",
  },
  {
    title: "Smart Grid Motor Drive",
    status: "Ongoing",
    year: "2025",
    domain: "Power Systems",
    image:
      "https://images.unsplash.com/photo-1717386255773-1e3037c81788?w=1200&q=80&auto=format&fit=crop",
    desc: "Grid-tied high-efficiency drive with predictive load shaping and analytics.",
  },
  {
    title: "CubeSat Instrumentation Module",
    status: "Ongoing",
    year: "2025",
    domain: "Space",
    image:
      "https://images.unsplash.com/photo-1773750453939-1d1d7219f1be?w=1200&q=80&auto=format&fit=crop",
    desc: "Radiation-tolerant scientific payload with in-orbit reconfigurable firmware.",
  },
  {
    title: "AI Machine Vision for QC",
    status: "Completed",
    year: "2023",
    domain: "AI",
    image:
      "https://images.unsplash.com/photo-1644325349124-d1756b79dd42?w=1200&q=80&auto=format&fit=crop",
    desc: "Sub-pixel defect detection running on edge for high-throughput factories.",
  },
  {
    title: "Solar-Coupled BLDC Pump",
    status: "Future Concept",
    year: "2026",
    domain: "Renewable",
    image:
      "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=80&auto=format&fit=crop",
    desc: "MPPT-integrated BLDC pumping system for off-grid agriculture and rural water.",
  },
];

export const CAREERS = [
  {
    role: "Senior Embedded Systems Engineer",
    type: "Full-time",
    location: "Onsite",
    department: "Engineering",
  },
  {
    role: "Motor Design Engineer",
    type: "Full-time",
    location: "Onsite",
    department: "R&D",
  },
  {
    role: "AI / Machine Learning Engineer",
    type: "Full-time",
    location: "Hybrid",
    department: "AI Systems",
  },
  {
    role: "UAV Flight Software Engineer",
    type: "Full-time",
    location: "Onsite",
    department: "Aviation",
  },
  {
    role: "Research Intern — Robotics",
    type: "Internship",
    location: "Onsite",
    department: "R&D",
  },
  {
    role: "Research Fellow — Space Sciences",
    type: "Fellowship",
    location: "Onsite",
    department: "Space",
  },
];

export const NEWS = [
  {
    date: "Feb 12, 2026",
    tag: "R&D",
    title: "Akyvia unveils AK-1200 precision BLDC motor for aerospace actuation",
    excerpt:
      "Our new precision motor line delivers >92% efficiency and integrates with our MX-9 controller.",
  },
  {
    date: "Jan 30, 2026",
    tag: "Space",
    title: "Partnership announced for CubeSat scientific payload program",
    excerpt:
      "Akyvia joins an academic consortium to develop reconfigurable scientific instruments for orbit.",
  },
  {
    date: "Jan 15, 2026",
    tag: "Training",
    title: "2026 Summer Research Fellowship applications now open",
    excerpt:
      "Mentored, project-based fellowships in AI, robotics, aviation and space sciences.",
  },
  {
    date: "Dec 20, 2025",
    tag: "AI",
    title: "NX-1 Edge AI Node achieves 40 TOPS at 15W in production trials",
    excerpt:
      "Field trials in three factories demonstrate significant gains in defect detection accuracy.",
  },
];

export const STATS = [
  { k: "18+", v: "Research Domains" },
  { k: "40+", v: "Products & Systems" },
  { k: "12", v: "Active Projects" },
  { k: "1000+", v: "Trained Engineers" },
];
